import React, { useState } from 'react';
import {
  Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, TextField, Typography,
} from '@mui/material';
import { SalesTeamService } from '../../../services/salesTeam.service';
import { ReassignType } from '../../../interfaces/sales/salesTeam.interface';
import { useToast } from '../../../context/ToastContext';
import SalespersonSelect from './SalespersonSelect';

interface Props {
  open: boolean;
  type: ReassignType;
  id: string;
  /** What is being handed over, e.g. "Order SO-0007". */
  title: string;
  currentName?: string | null;
  onClose: () => void;
  onDone: () => void;
}

/** S0 reassign: pick any active employee of this institution, or unassign. Needs SALES_REASSIGN (the caller gates the button). */
const ReassignDialog: React.FC<Props> = ({ open, type, id, title, currentName, onClose, onDone }) => {
  const toast = useToast();
  const [target, setTarget] = useState<string | null>(null);
  const [unassign, setUnassign] = useState(false);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const canSave = unassign || !!target;

  const save = async () => {
    try {
      setSaving(true);
      await SalesTeamService.reassign(type, id, unassign ? null : target, note);
      toast.showSuccess(unassign ? 'Unassigned' : 'Reassigned');
      onDone();
      onClose();
    } catch (e) {
      toast.showApiError(e, 'Failed to reassign');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" data-help-page="sales.reassign.page">
      <DialogTitle>Reassign: {title}</DialogTitle>
      <DialogContent data-help-section="sales.reassign.section.form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Now: {currentName || 'Unassigned'}. The previous salesperson stops seeing this record.
        </Typography>
        <SalespersonSelect data-help="sales.reassign.target" label="New salesperson" size="medium" value={target} onChange={setTarget} disabled={unassign} />
        <FormControlLabel data-help="sales.reassign.unassign"
          control={<Checkbox checked={unassign} onChange={e => setUnassign(e.target.checked)} />}
          label="Unassign (no salesperson)"
        />
        <TextField data-help="sales.reassign.note" label="Note (optional)" size="small" value={note} onChange={e => setNote(e.target.value)} inputProps={{ maxLength: 500 }} />
      </DialogContent>
      <DialogActions>
        <Button data-help="sales.reassign.cancel" onClick={onClose}>Cancel</Button>
        <Button data-help="sales.reassign.submit" variant="contained" onClick={save} disabled={!canSave || saving}>Reassign</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReassignDialog;
